// StarterPlanOverview.tsx - Updated to show data for all plans
import {useContext, useEffect, useState, useRef, useMemo} from 'react'
import TreatmentProgress from '../../Patients/NewPatientProfile/components/TreatmentProgress'
import Page from 'components/page/Page'
import {useSelector} from 'react-redux'
import {RootState} from 'redux/store'
import {postApiDataTreatmentPlan} from 'redux/Slices/AppSlice/PatientProfile/TreatmentPlan/TreatmentPlan'
import useDispatchAction from '@hooks/useDispatchAction'
import When from 'components/when/When'
import treatmentPlanStatusConstants from '@constants/treatmentPlanStatus.constants'
import jawType from '@constants/jawType'
import {useNavigate, useParams, useSearchParams} from 'react-router-dom'
import {AuthContext} from 'context/AuthContext'
import {safeParseInt} from 'utils/ConstFunctions'
import productTypes from '@constants/productTypes'
import dayjs from 'dayjs'
import patientOverviewAlignerActionFilterConstants from '@constants/patientOverviewAlignerActionFilterConstants.constants'
import {
  getPatientTimeline,
  setSelectedFilter,
} from 'redux/Slices/AppSlice/LeadsProfile/LeadsProfile.slice'
import BorderedCardForDashBoardCards from 'screens/Dashboard/components/BorderedCard'
import CaretRightIcon from 'assets/icons/CaretRightIcon'
import {getDueMessageText} from 'utils/getDueMessageText'
import GetCompilance from 'screens/AlignerPatientAnalytics/components/GetCompilance'
import cn from '@utils/cn'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import hasValue from 'utils/hasValue'
import useProfileBasePath from '@hooks/useProfileBasePath'
import {useTreatmentPlanFilter} from '@hooks/useTreatmentPlanFilter'
import EmptyDataCard from 'components/atom/EmptyState/EmptyDataCard'

dayjs.extend(isSameOrAfter)
export const jawTypeMap = {
  [jawType.BOTH]: 'Both',
  [jawType.LOWER]: 'Lower',
  [jawType.UPPER]: 'Upper',
}

type FilterOption = {
  key: keyof typeof patientOverviewAlignerActionFilterConstants
  label: string
}

const FILTER_OPTIONS: FilterOption[] = [
  {
    key: patientOverviewAlignerActionFilterConstants.ALL_ALIGNERS,
    label: 'All Aligners',
  },
  {
    key: patientOverviewAlignerActionFilterConstants.CURRENT_ALIGNER,
    label: 'Current Aligner',
  },
  {
    key: patientOverviewAlignerActionFilterConstants.PENDING_UPDATES,
    label: 'Pending Updates',
  },
  {
    key: patientOverviewAlignerActionFilterConstants.ISSUES_REPORTED,
    label: 'Issues Reported',
  },
  {
    key: patientOverviewAlignerActionFilterConstants.ALIGNER_CHECKINS,
    label: 'Aligner Check-ins',
  },
  {
    key: patientOverviewAlignerActionFilterConstants.ALIGNER_CHANGES,
    label: 'Aligner Changes',
  },
]

const StarterPlanOverview = ({
  treatmentPlanId,
}: {
  treatmentPlanId?: number | null
}) => {
  const {dispatchAction} = useDispatchAction()
  const {userId} = useContext(AuthContext)
  const [searchParams] = useSearchParams()
  const {patientId} = useParams<{patientId: string}>()
  const {isPastPlan, getPlanDataById, getTreatmentStatusByPlanId, isPlanActive} = useTreatmentPlanFilter()
  const navigate = useNavigate()
  const profileBasePath = useProfileBasePath()
  
  // Use a ref to track the previous treatmentPlanId
  const prevTreatmentPlanIdRef = useRef<number | null | undefined>(treatmentPlanId)
  const [isDataLoaded, setIsDataLoaded] = useState(false)

  // Get treatment status from the plan data using the hook
  const treatmentStatus = useMemo(() => {
    if (treatmentPlanId) {
      return getTreatmentStatusByPlanId(treatmentPlanId);
    }
    return null;
  }, [treatmentPlanId, getTreatmentStatusByPlanId]);

  // Check if plan is active
  const isActivePlan = useMemo(() => {
    if (treatmentPlanId) {
      return isPlanActive(treatmentPlanId);
    }
    return true;
  }, [treatmentPlanId, isPlanActive]);

  const patientTimelineFilter = searchParams.get('patient_timeline_filter')
  useEffect(() => {
    const filterParam = searchParams.get('patient_timeline_filter')

    const initialFilter =
      FILTER_OPTIONS.find((option) => option.key === filterParam) ?? FILTER_OPTIONS[1]

    dispatchAction(setSelectedFilter(initialFilter))
  }, [patientTimelineFilter])

  // Effect to fetch treatment plan data when treatmentPlanId changes
  useEffect(() => {
    const parsedPatientId = safeParseInt(patientId)
    if (!parsedPatientId || !treatmentPlanId) return

    // Check if treatmentPlanId has changed
    if (prevTreatmentPlanIdRef.current !== treatmentPlanId) {
      prevTreatmentPlanIdRef.current = treatmentPlanId
      setIsDataLoaded(false) // Reset data loaded state
      
      // Fetch treatment plan data with treatment_plan_id
      const postData: any = {
        data: {
          patient_id: parsedPatientId,
          treatment_plan_id: treatmentPlanId, // Pass treatment_plan_id
        },
      }
      dispatchAction(postApiDataTreatmentPlan(postData))
    }
  }, [dispatchAction, patientId, treatmentPlanId])

  const selectedFilter = useSelector((state: RootState) => state.leadsProfile.selectedFilter)

  const handleFilterChanges = (
    filter: keyof typeof patientOverviewAlignerActionFilterConstants
  ) => {
    const filterOption = FILTER_OPTIONS.find((opt) => opt.key === filter)!

    dispatchAction(setSelectedFilter(filterOption))
    dispatchAction(
      getPatientTimeline({
        doctor_id: safeParseInt(userId),
        patient_id: safeParseInt(patientId),
        filter,
        treatment_plan_id: treatmentPlanId, // Pass treatment_plan_id
      })
    )

    navigate(`${profileBasePath}/${patientId}/aligner-tracking`, {
      state: {selectedFilterKey: filter},
    })
  }
  
  const {loading: loadingTreatmentPlan, data: dataTreatmentPlan}: any = useSelector(
    (state: RootState) => state.apiTreatmentPlan
  )
  const {patientTimeline, dataLeadsOverview} = useSelector((state: RootState) => state.leadsProfile)
  const [, setForceAlignerDisabled] = useState(false)

  // Set data loaded when treatment plan data is available
  useEffect(() => {
    if (dataTreatmentPlan && !loadingTreatmentPlan) {
      setIsDataLoaded(true)
    }
  }, [dataTreatmentPlan, loadingTreatmentPlan])

  // Effect to fetch patient timeline when treatmentPlanId changes
  useEffect(() => {
    const parsedDoctorId = safeParseInt(userId)
    const parsedPatientId = safeParseInt(patientId)
    if (!parsedDoctorId || !parsedPatientId || !treatmentPlanId) return

    dispatchAction(
      getPatientTimeline({
        doctor_id: parsedDoctorId,
        patient_id: parsedPatientId,
        filter: selectedFilter.key,
        treatment_plan_id: treatmentPlanId, // Pass treatment_plan_id
      })
    )
  }, [dispatchAction, selectedFilter, userId, patientId, treatmentPlanId])

  const overdue = patientTimeline?.patient_profile_overview_response?.over_due ?? 0
  const isManualTracking = dataLeadsOverview?.tracking?.type === 'MANUAL'
  const pendingActionsCount =
    patientTimeline?.patient_profile_overview_response?.pending_actions_count

  const patientTimeLineClassName = 'w-full flex flex-col gap-4'

  useEffect(() => {
    if (loadingTreatmentPlan) return
    if (!hasValue(dataTreatmentPlan?.aligner_journeys?.[0])) {
      setForceAlignerDisabled(false)
      return
    }

    const alignerJourneyData: any = dataTreatmentPlan?.aligner_journeys?.[0]
    const currentAligner =
      alignerJourneyData?.aligners?.find(
        (aligner: any) => aligner?.sr_no === alignerJourneyData?.current_aligner_no
      ) ?? null

    if (!currentAligner) {
      setForceAlignerDisabled(false)
      return
    }

    const isCurrentAlignerStartToday = currentAligner?.start_date
      ? dayjs(currentAligner.start_date).isSame(dayjs(), 'day')
      : false
    setForceAlignerDisabled(isCurrentAlignerStartToday)
  }, [dataTreatmentPlan, loadingTreatmentPlan])

  // Show empty state for past plans on Overview tab
  // FIX: Only show empty state if it's a past plan AND we have confirmed data loading
  if (isPastPlan && isDataLoaded) {
    return <EmptyDataCard title='Past Plan Overview' description='Overview is not available for past treatment plans' />
  }

  // Show loading state while data is being fetched


  // Check if we have data to display
  const hasData = patientTimeline?.patient_profile_overview_response;
  if (!hasData) {
    return (
      <div className='w-full flex justify-center items-center py-12'>
        <div className='text-gray-500'>No data available for this treatment plan</div>
      </div>
    )
  }

  return (
    <Page title={''} loading={loadingTreatmentPlan}>
      <div className='w-full'>
        <div className={patientTimeLineClassName}>
          <div className='flex flex-col md:flex-row gap-2'>
            <div className='md:w-5/6 w-full'>
              <TreatmentProgress
                productType={productTypes.ALIGNERS}
                status={treatmentStatus}
                currentAligner={
                  patientTimeline?.patient_profile_overview_response?.current_aligner ?? 0
                }
                totalAligners={
                  patientTimeline?.patient_profile_overview_response?.total_aligner ?? 0
                }
                key={treatmentPlanId} // Force re-render when treatmentPlanId changes
              />
            </div>
            <div className='flex w-full gap-2'>
              <BorderedCardForDashBoardCards className='w-full h-auto'>
                <div className='flex flex-col gap-1'>
                  <GetCompilance
                    app_invite_status={
                      overdue < -7 ? 'NEED_ATTENTION' : overdue < 0 ? 'AT_RISK' : 'ON_TRACK'
                    }
                  />

                  <div className={cn('text-base text-black')}>
                    {getDueMessageText({
                      offSetDays: overdue,
                    })}
                  </div>
                </div>
              </BorderedCardForDashBoardCards>
              <BorderedCardForDashBoardCards className='w-full h-auto'>
                <div className='flex flex-col gap-1'>
                  <div className='text-sm text-textColor font-medium'>Aligner updates</div>
                  <div className='text-base text-black'>{pendingActionsCount} pending review</div>
                  <When
                    isTrue={
                      !isManualTracking &&
                      pendingActionsCount != undefined &&
                      pendingActionsCount > 0
                    }
                  >
                    <button
                      type='button'
                      className='text-primaryColor text-sm font-medium mt-1 flex items-center gap-1 hover:underline'
                      onClick={() => {
                        handleFilterChanges('PENDING_UPDATES')
                      }}
                    >
                      View all pending updates <CaretRightIcon color='#735BF2' />
                    </button>
                  </When>
                </div>
              </BorderedCardForDashBoardCards>
            </div>
          </div>
        </div>
      </div>
    </Page>
  )
}

export default StarterPlanOverview